import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getCourseCodes, 
  generateCourseCodes, 
  deleteCourseCode,
  getCourses
} from '../../services/courseService';
import { CourseCode, Course } from '../../types';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Alert, 
  Spinner, 
  Form, 
  Modal, 
  Badge, 
  InputGroup,
  Toast,
  ToastContainer
} from 'react-bootstrap';
import { 
  FaPlus, 
  FaTrash, 
  FaKey, 
  FaCopy, 
  FaDownload,
  FaFilter,
  FaCheck,
  FaExclamationTriangle,
  FaCalendarAlt
} from 'react-icons/fa';

const CoursesCodesPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [courseCodes, setCourseCodes] = useState<CourseCode[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [codeCount, setCodeCount] = useState<string>('1');
  const [expiryDays, setExpiryDays] = useState<string>('30');
  const [searchTerm, setSearchTerm] = useState('');
  const [includeUsed, setIncludeUsed] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [generatedCodes, setGeneratedCodes] = useState<CourseCode[]>([]);
  const [showGeneratedCodesModal, setShowGeneratedCodesModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [includeUsed]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!currentUser) {
        setError('يجب تسجيل الدخول أولاً');
        return;
      }
      
      const [fetchedCodes, fetchedCourses] = await Promise.all([
        getCourseCodes(currentUser.uid, includeUsed),
        getCourses()
      ]);
      
      setCourseCodes(fetchedCodes);
      setCourses(fetchedCourses);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('حدث خطأ أثناء جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCodes = async () => {
    if (!selectedCourseId) {
      setError('يرجى اختيار كورس');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await generateCourseCodes(
        selectedCourseId, 
        parseInt(codeCount) || 1, 
        parseInt(expiryDays) || 30
      );
      
      if (result && result.codes) {
        setGeneratedCodes(result.codes);
        setShowGeneratedCodesModal(true);
      }
      
      await fetchData();
      setShowGenerateModal(false);
      resetForm();
      showToastMessage(`تم إنشاء ${codeCount} كود بنجاح`);
    } catch (error) {
      console.error('Error generating codes:', error);
      setError('حدث خطأ أثناء إنشاء الأكواد');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCode = async (codeId: string) => {
    try {
      setLoading(true);
      setError(null);
      await deleteCourseCode(codeId);
      await fetchData();
      showToastMessage('تم حذف الكود بنجاح');
    } catch (error) {
      console.error('Error deleting code:', error);
      setError('حدث خطأ أثناء حذف الكود');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedCourseId('');
    setCodeCount('1');
    setExpiryDays('30');
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    showToastMessage('تم نسخ الكود');
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const formatDate = (timestamp?: number | null) => {
    if (!timestamp) return 'غير محدد';
    return new Date(timestamp).toLocaleDateString('ar-EG');
  };

  const getStatusBadge = (code: CourseCode) => {
    if (code.isUsed) {
      return <Badge bg="danger">مستخدم</Badge>;
    } else if (code.isExpired) {
      return <Badge bg="secondary">منتهي الصلاحية</Badge>;
    } else {
      return <Badge bg="success">متاح</Badge>;
    }
  };

  const handleExportCodes = () => {
    const csvContent = 
      "data:text/csv;charset=utf-8," + 
      "Code,Course,Status,Expiry Date\n" + 
      courseCodes.map(code => {
        const course = courses.find(c => c.id === code.courseId);
        const status = code.isUsed ? 'مستخدم' : (code.isExpired ? 'منتهي الصلاحية' : 'متاح');
        return `${code.code},"${course?.title || 'Unknown'}",${status},${formatDate(code.expiryDate)}`;
      }).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `course-codes-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToastMessage('تم تصدير الأكواد بنجاح');
  };

  const filteredCodes = courseCodes.filter(code => {
    if (!searchTerm) return true;
    
    const course = courses.find(c => c.id === code.courseId);
    return (
      code.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (course && course.title?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  return (
    <Container fluid className="py-4">
      {loading && !courseCodes.length && (
        <div className="text-center my-4">
          <Spinner animation="border" role="status" />
          <p className="mt-2">جاري تحميل البيانات...</p>
        </div>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3">
        <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)} 
          delay={3000} 
          autohide
          bg="success"
          className="toast-message"
        >
          <Toast.Header closeButton>
            <strong className="me-auto">إشعار</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
      
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <h1 className="mb-4">إدارة أكواد الكورسات</h1>
        </Col>
      </Row>
      
      {/* Codes List */}
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h2>أكواد التفعيل</h2>
            </Col>
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <FaKey />
                </InputGroup.Text>
                <Form.Control 
                  type="text" 
                  placeholder="ابحث عن الأكواد..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Check 
                type="switch"
                id="include-used-codes"
                label="عرض الأكواد المستخدمة"
                checked={includeUsed}
                onChange={(e) => setIncludeUsed(e.target.checked)}
              />
            </Col>
            <Col md={4} className="d-flex justify-content-end">
              <Button 
                variant="outline-secondary" 
                className="me-2"
                onClick={handleExportCodes}
                disabled={filteredCodes.length === 0}
              >
                <FaDownload className="me-1" /> تصدير الأكواد
              </Button>
              <Button 
                variant="primary" 
                onClick={() => setShowGenerateModal(true)}
              >
                <FaPlus className="me-1" /> إنشاء أكواد جديدة
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>الكود</th>
                  <th>الكورس</th>
                  <th>تاريخ الإنشاء</th>
                  <th>تاريخ الانتهاء</th>
                  <th>الحالة</th>
                  <th>المستخدم</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredCodes.map((code) => {
                  const course = courses.find(c => c.id === code.courseId);
                  return (
                    <tr key={code.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="code-text me-2">{code.code}</span>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => handleCopyCode(code.code)}
                            title="نسخ الكود"
                          >
                            <FaCopy />
                          </Button>
                        </div>
                      </td>
                      <td>{course?.title || 'غير معروف'}</td>
                      <td>{formatDate(code.createdAt)}</td>
                      <td>{formatDate(code.expiryDate)}</td>
                      <td>
                        {getStatusBadge(code)}
                      </td>
                      <td>{code.usedBy || '-'}</td>
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteCode(code.id || '')}
                          disabled={code.isUsed}
                          title={code.isUsed ? 'لا يمكن حذف كود مستخدم' : 'حذف الكود'}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {filteredCodes.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'لا توجد أكواد متاحة'
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      {/* Generate Codes Modal */}
      <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>إنشاء أكواد جديدة</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>اختر الكورس</Form.Label>
              <Form.Select
                value={selectedCourseId}
                onChange={(e) => setSelectedCourseId(e.target.value)}
                required
              >
                <option value="">اختر الكورس...</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>عدد الأكواد</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="100"
                value={codeCount}
                onChange={(e) => setCodeCount(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                يمكنك إنشاء حتى 100 كود في المرة الواحدة
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>مدة صلاحية الأكواد (بالأيام)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="365"
                value={expiryDays}
                onChange={(e) => setExpiryDays(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                الحد الأقصى لمدة الصلاحية هو 365 يوم
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)}>
            إلغاء
          </Button>
          <Button
            variant="primary"
            onClick={handleGenerateCodes}
            disabled={!selectedCourseId || loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                جاري الإنشاء...
              </>
            ) : (
              'إنشاء الأكواد'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Generated Codes Modal */}
      <Modal 
        show={showGeneratedCodesModal} 
        onHide={() => setShowGeneratedCodesModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>الأكواد التي تم إنشاؤها</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">تم إنشاء {generatedCodes.length} كود بنجاح. يمكنك نسخ هذه الأكواد أو تصديرها.</p>
          <div className="generated-codes-container p-3 border rounded bg-light">
            {generatedCodes.map((code, index) => (
              <div key={index} className="code-item d-flex justify-content-between align-items-center mb-2 p-2 border-bottom">
                <span className="code-text">{code.code}</span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleCopyCode(code.code)}
                  title="نسخ الكود"
                >
                  <FaCopy className="me-1" /> نسخ
                </Button>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => {
              const codesText = generatedCodes.map(code => code.code).join('\n');
              navigator.clipboard.writeText(codesText);
              showToastMessage('تم نسخ جميع الأكواد');
            }}
          >
            <FaCopy className="me-1" /> نسخ جميع الأكواد
          </Button>
          <Button variant="primary" onClick={() => setShowGeneratedCodesModal(false)}>
            إغلاق
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .code-text {
          font-family: monospace;
          font-size: 1.1em;
          background-color: #f8f9fa;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .table th {
          white-space: nowrap;
        }
        .generated-codes-container {
          max-height: 300px;
          overflow-y: auto;
        }
        .code-item:last-child {
          border-bottom: none !important;
        }
        .toast-message {
          color: #fff;
        }
      `}</style>
    </Container>
  );
};

export default CoursesCodesPage;
